import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { deleteIndustry, fetchIndustryReferences } from '../../client/industryAdminClient';
import { denialHint } from '../../utils/authorization';
import { describeReferences } from './industryAdmin';

/**
 * Delete an industry. Two gates, both required:
 *   1. nothing may still reference it — the dialog lists what does, and offers no delete
 *      until the admin has moved or deleted those;
 *   2. the admin types the industry code to confirm.
 *
 * The service repeats the reference check inside the delete, so (1) is what the admin sees,
 * not what keeps the data safe.
 */
export function DeleteIndustryModal({
  industry,
  onDismiss,
  onDeleted,
}: {
  industry: string;
  onDismiss: () => void;
  onDeleted: () => void;
}) {
  const [refs, setRefs] = useState<Record<string, number> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRefs(null);
    setLoadError(null);
    try {
      setRefs(await fetchIndustryReferences(industry));
    } catch (e) {
      setLoadError(denialHint(e, 'en') ?? 'Could not check what still uses this industry.');
    }
  }, [industry]);

  useEffect(() => {
    void load();
  }, [load]);

  const blockers = refs ? describeReferences(refs) : [];
  const deletable = refs !== null && blockers.length === 0;

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      await deleteIndustry(industry);
      onDeleted();
    } catch (e) {
      if ((e as { name?: string } | null)?.name === 'ConflictException') {
        // Something was added to the industry after the dialog opened.
        setTyped('');
        await load();
      } else {
        setError(denialHint(e, 'en') ?? 'Could not delete the industry.');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!pending) onDismiss();
      }}
      header={`Delete industry ${industry}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={pending}>
              {deletable ? 'Cancel' : 'Close'}
            </Button>
            {deletable && (
              <Button
                variant="primary"
                disabled={typed !== industry}
                loading={pending}
                onClick={() => void submit()}
              >
                Delete
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {loadError && (
          <Alert type="error" action={<Button onClick={() => void load()}>Retry</Button>}>
            {loadError}
          </Alert>
        )}
        {!loadError && refs === null && (
          <Box textAlign="center">
            <Spinner /> Checking what still uses {industry}…
          </Box>
        )}
        {blockers.length > 0 && (
          <Alert type="warning" header="Still in use">
            {industry} cannot be deleted yet. Move these to another industry or delete them first:
            <ul>
              {blockers.map((b) => (
                <li key={b.label}>
                  {b.label}: {b.count}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {deletable && (
          <>
            <Alert type="warning">
              The industry and its node mapping are removed, and its opportunities stop syncing.
              This cannot be undone from this page.
            </Alert>
            <FormField label={`Type ${industry} to confirm`}>
              <Input value={typed} onChange={({ detail }) => setTyped(detail.value)} ariaLabel="Industry code" />
            </FormField>
          </>
        )}
        {error && (
          <Alert type="error" header="Could not delete">
            {error}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
